<template>
  <div class="user-info-item">
    <div>
      <h3 :class="{ 'text--ellipsis': truncateText }">{{ title }}</h3>
      <a v-if="attachmentUrl && !subtitle" :href="attachmentUrl" target="_blank"
        class="icon icon-attachment margin-h--s text text--link" :title="$t('buttons.download')"></a>
    </div>

    <div v-if="subtitle" class="subtitle-container">
      <span class="text text--secondary">{{ subtitle }}</span>
      <a v-if="attachmentUrl" data-cy="user-info-item-attachment" @click="downloadFile" target="_blank"
        class="icon icon-attachment margin-h--s text text--link" :title="$t('buttons.download')"></a>
    </div>
    <div class="text text--secondary" :class="{ 'text--line-clamp-3': truncatedContent }" :title="titleHtml"
      @click="toggle" v-html="contentHtml"></div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from "vue";
import { toHtml, toTitleHtml } from "@/utils/stringUtils";
import useEmployeeService from "@/services/useEmployeeService";
import { FileAttachment } from "@/models/fileAttachment";

interface Props {
  title?: string;
  subtitle?: string;
  content?: string;
  truncateText?: boolean;
  employeeId: number;
  experienceId?: number;
  contentSection?: string;
  attachment?: FileAttachment;
}
const props = defineProps<Props>();

const titleHtml = computed(() => toTitleHtml(props.content));
const contentHtml = computed(() => toHtml(props.content));
const truncatedContent = ref<boolean>(props.truncateText);
const contentSection = ref<string>(props.contentSection || "");
const fileNameAndExtension = ref<string>(props.attachment?.fileName || "");

const { getJobExperienceAttachment, getEducationAttachment } = useEmployeeService();

const attachmentUrl = ref<string>(fileNameAndExtension.value);

const toggle = () => {
  if (!props.truncateText) {
    return;
  }

  truncatedContent.value = !truncatedContent.value;
};

const downloadFile = async () => {
  if (!attachmentUrl.value) {
    return;
  }

  if (props.employeeId && props?.experienceId) {
    let blob: Blob;
    try {
      switch (contentSection.value) {
        case 'education':
          blob = await getEducationAttachment(props.employeeId, props.experienceId);
          break;
        default:
          blob = await getJobExperienceAttachment(props.employeeId, props.experienceId);
          break;
      }
      attachmentUrl.value = URL.createObjectURL(blob);
    } catch {
      attachmentUrl.value = "";
    }

    const link = document.createElement("a");
    link.href = attachmentUrl.value;
    link.download = fileNameAndExtension.value;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

onUnmounted(() => {
  if (attachmentUrl.value) {
    URL.revokeObjectURL(attachmentUrl.value);
  }
});
</script>

<style scoped lang="scss">
.user-info-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}
</style>
